"use client";

import React, { useEffect, useRef, useState } from "react";
import { captureProductClientEvent } from "@/lib/analytics/product-client-event";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import type { InAppBrowserDetection } from "@/lib/in-app-browser";

import { Button } from "@/components/ui/button";
import { copyTextToClipboard } from "@/lib/clipboard";
import { Note } from "@/components/ui/note";

interface InAppBrowserNoticeProps {
  detection: InAppBrowserDetection;
}

/**
 * Builds an Android intent URL that re-opens the current page in the user's
 * default browser (Chrome, Samsung Internet, Firefox, …), escaping the host
 * app's embedded webview. Returns null when there's no `window` (SSR) so
 * callers can omit the button.
 *
 * No `package=` is set on purpose: that lets Android route the intent to
 * whichever browser the user has set as default, rather than forcing Chrome.
 * The host app's webview isn't a browser, so it won't appear as a candidate.
 * `action` + `category` make this an explicit "view this URL in a browser"
 * request, which the major in-app browsers (LinkedIn, Gmail, Slack, Instagram,
 * Facebook) honor.
 *
 * `host + pathname + search` only — the `#` would collide with the intent's
 * own fragment syntax, and auth routes carry no meaningful hash. `search` is
 * already percent-encoded by the browser. `S.browser_fallback_url` is the last
 * resort if the intent can't be resolved at all (effectively never, since every
 * Android device ships with a browser).
 */
function buildExternalBrowserIntentUrl(): string | null {
  if (typeof window === "undefined") return null;
  const { host, pathname, search } = window.location;
  const fallback = encodeURIComponent(window.location.href);
  return `intent://${host}${pathname}${search}#Intent;scheme=https;action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;S.browser_fallback_url=${fallback};end`;
}

interface IosOpenInstruction {
  /** Where the menu lives in this app, e.g. "the ••• menu (top right)". */
  menu: string;
  /** The exact menu-item label to tap — wording varies per app. */
  action: string;
}

/**
 * iOS has no reliable programmatic escape from a webview, so the best we can do
 * is tell the user exactly where their app hides the "open externally" option.
 * Wording varies per app and across app versions; the default covers the
 * common case (and any app we couldn't identify).
 */
function getIosOpenInstruction(source: string | null): IosOpenInstruction {
  switch (source) {
    case "LinkedIn":
      return { menu: "the ••• menu (top right)", action: "Open in Safari" };
    case "Instagram":
      return { menu: "the ••• menu (top right)", action: "Open in external browser" };
    case "Facebook":
    case "Messenger":
      return { menu: "the ••• menu", action: "Open in Safari" };
    case "Snapchat":
      return { menu: "the ••• menu", action: "Open in Browser" };
    case "LINE":
      return { menu: "the menu (top right)", action: "Open in default browser" };
    case "Slack":
      return { menu: "the ••• menu", action: "Open in Safari" };
    case "Twitter":
      return { menu: "the share menu", action: "Open in Safari" };
    case "Google App":
      return { menu: "the ••• menu", action: "Open in Safari" };
    default:
      return { menu: "the ••• or share menu", action: "Open in Safari" };
  }
}

/**
 * Shown on signup/login in place of the social-OAuth buttons when the user is
 * inside an in-app/embedded browser (webview). Google's "Use secure browsers"
 * policy blocks its OAuth consent screen in webviews, so a "Continue with
 * Google" button would dead-end at a 403. Escape hatches, in priority order:
 *
 *   1. Email / magic-code (always works in a webview) — the form rendered just
 *      below this notice by the parent. We point the user at it prominently.
 *   2. Android: a button that re-opens the page in the user's default browser
 *      via an intent URL.
 *   3. iOS: no reliable programmatic escape — app-specific instructions to open
 *      in Safari + a copy-link button.
 */
export default function InAppBrowserNotice({ detection }: InAppBrowserNoticeProps) {
  const { source, platform } = detection;
  const [copied, setCopied] = useState(false);
  const iosInstruction = getIosOpenInstruction(source);
  // The "Link copied" label auto-reverts after a delay; track the timer so we
  // can clear it on unmount and avoid a setState-after-unmount warning.
  const copyResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Capture once when the notice is shown so we can size volume per app.
  useEffect(() => {
    captureProductClientEvent(ANALYTICS_EVENTS.AUTH_IN_APP_BROWSER_DETECTED, {
      app_source: source,
      platform,
    });
  }, [source, platform]);

  useEffect(() => {
    return () => {
      if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
    };
  }, []);

  const appLabel = source ? `${source}'s built-in browser` : "This app's built-in browser";

  function captureEscapeClick(method: "open_in_browser" | "copy_link") {
    captureProductClientEvent(ANALYTICS_EVENTS.AUTH_IN_APP_BROWSER_ESCAPE_CLICKED, {
      method,
      app_source: source,
      platform,
    });
  }

  async function handleCopyLink() {
    if (typeof window === "undefined") return;
    captureEscapeClick("copy_link");
    const ok = await copyTextToClipboard(window.location.href, {
      successMessage: "Link copied. Paste it into your browser",
      errorMessage: "Couldn't copy the link. Long-press the address bar to copy it.",
    });
    if (!ok) return;
    setCopied(true);
    if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
    copyResetTimer.current = setTimeout(() => setCopied(false), 2500);
  }

  function handleOpenInBrowser() {
    captureEscapeClick("open_in_browser");
    const intentUrl = buildExternalBrowserIntentUrl();
    if (intentUrl) {
      window.location.href = intentUrl;
    }
  }

  return (
    <div
      data-testid="in-app-browser-notice"
      className="rounded-lg border border-border bg-muted p-4 space-y-3"
    >
      <div className="flex items-start gap-3">
        <svg
          className="mt-0.5 h-5 w-5 shrink-0 text-foreground"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            Google sign-in isn&apos;t available here
          </p>
          <Note size="sm">
            {appLabel} can&apos;t open Google sign-in. Use your email below. It works the same.
          </Note>
        </div>
      </div>

      <div className="space-y-2">
        {platform === "android" && (
          <Button
            type="button"
            onClick={handleOpenInBrowser}
            data-testid="in-app-browser-open-browser"
            className="w-full"
          >
            Open in browser
          </Button>
        )}

        {platform === "ios" && (
          <Note>
            Prefer Google? Tap {iosInstruction.menu} and choose{" "}
            <span className="font-medium">&ldquo;{iosInstruction.action}&rdquo;</span>.
          </Note>
        )}

        {/* The link itself — always offered so the user can open this page in a
            real browser, whatever their platform. */}
        <Button
          variant="outline"
          type="button"
          onClick={handleCopyLink}
          data-testid="in-app-browser-copy-link"
          className="w-full"
        >
          {copied ? "Link copied" : "Copy link"}
        </Button>
      </div>
    </div>
  );
}
